import {defineStore} from 'pinia'
import {useAppStore} from '@/stores/appStore'
import {useComponentsStore} from '@/stores/components'
import {useFieldsetsStore} from '@/stores/fieldsets'
import api from '@/services/api'

export const useComponentFieldsStore = defineStore('componentFields', {
    state: () => ({
        componentId: 0,
        component: {},
        fieldsets: [],
        fieldsetFormData: {},
        activeFieldsetId: 0,
        items: [],
        getModifiedData: undefined,
        
        // Component fields only
        activeEditId: undefined
    }),
    
    getters: {
        isForm: (state) => {
            return state.component.navigationGroup && state.component.navigationGroup === 'Forms'
        },
        isContentType: (state) => {
            return state.component.tableName && state.component.tableName.indexOf('component_') === 0
        },
        // Convert strings to camelcase and removes special chars
        toCamelCase: () => (str) => {
            str = str.replace(/[^a-zA-Z 0-9]+/g, '') // Remove all special characters
            str = str.substring(0, 1).toLowerCase() + str.substring(1) // First character to lowercase
            // Replace all double spaces left by removing special chars
            while (str.match('  ')) {
                str = str.replace('  ', ' ')
            }
            
            return str.replace(/ (.)/g, (match, group1) => {
                return group1.toUpperCase()
            })
        },
        getFields: (state) => (type) => {
            const fields = [
                'title', // Label
                'columnName',
                'hidden',
                'readonly',
            ]
            
            // Create attribute forms
            if (state.activeEditId === 0) {
                return [
                    ...fields,
                    'type'
                ]
            }
            
            switch (type) {
                case 'checkbox':
                    return [
                        ...fields,
                        'checked',
                        'required',
                        'helpText',
                    ]
                
                case 'dateDate':
                case 'datetime-local':
                case 'time':
                    return [
                        ...fields,
                        'required',
                        'step',
                        'helpText',
                    ]
                
                case 'email':
                case 'tel':
                case 'url':
                    return [
                        ...fields,
                        'placeholder',
                        'required',
                        'uniqueValuesOnly',
                        'helpText',
                    ]
                
                case 'password':
                case 'text':
                    return [
                        ...fields,
                        'placeholder',
                        'minlength',
                        'maxlength',
                        'pattern',
                        'required',
                        'size',
                        'uniqueValuesOnly',
                        'helpText',
                    ]
                
                case 'file':
                case 'image':
                    return [
                        ...fields,
                        'placeholder',
                        'required',
                        'helpText',
                    ]
                
                case 'internalLink':
                    return [
                        ...fields,
                        'targetContent',
                        'required',
                        'helpText',
                    ]
                
                case 'number':
                    return [
                        ...fields,
                        'min',
                        'max',
                        'step',
                        'required',
                        'defaultValue',
                        'helpText',
                    ]
                
                case 'range':
                    return [
                        ...fields,
                        'min',
                        'max',
                        'step',
                        'helpText',
                    ]
                
                case 'relationshipOneToMany':
                case 'relationshipManyToMany':
                    return [
                        ...fields,
                        'categoryComponentId',
                        'required',
                        'helpText',
                    ]
                
                case 'select':
                    return [
                        ...fields,
                        'firstOption',
                        'options',
                        'required',
                        'helpText',
                    ]
                
                case 'textarea':
                    return [
                        ...fields,
                        'placeholder',
                        'required',
                        'minlength',
                        'maxlength',
                        'rows',
                        'cols',
                        'wrap',
                        'helpText',
                    ]
                
                case 'texteditor':
                    return [
                        ...fields,
                        'required',
                        'helpText',
                    ]
                
                default:
                    return fields
            }
        }
    },
    
    actions: {
        init({componentId}) {
            // Reset state
            this.items = []
            this.fieldsets = []
            this.componentId = componentId
            
            this.setComponent()
            this.setFieldsets()
            this.setItems()
        },
        
        setComponent() {
            const componentsStore = useComponentsStore()
            const component = componentsStore.items.find(o => o.id === this.componentId)
            this.component = component ? JSON.parse(JSON.stringify(component)) : {}
        },
        
        setFieldsets() {
            const fieldsetsStore = useFieldsetsStore()
            const fieldsets = fieldsetsStore.items.filter(o => o.componentId === this.componentId) || []
            this.fieldsets = JSON.parse(JSON.stringify(fieldsets))
        },
        
        async setItems() {
            const appStore = useAppStore()
            
            const params = {
                modulecomponentsID: this.componentId,
                modulecomponentsId: this.componentId,
                sort: 'displayOrder',
            }
            
            if (!appStore.alphaMode) {
                params.hidden = 0
            }
            
            if (this.activeFieldsetId) {
                params.fieldsetId = this.activeFieldsetId
            }
            
            const obj = await api.get('api/component/modulestructure', {params})
            let items = obj.data.items
            
            if (!appStore.alphaMode) {
                if (this.isContentType) {
                    items = items.filter(o => o.columnName !== 'contentId')
                } else if (this.isForm) {
                    items = items.filter(o => o.columnName !== 'emailMessageId')
                }
            }
            
            this.items = JSON.parse(JSON.stringify(items))
        },
        
        async deleteField({fieldId, router}) {
            await api.delete(`api/component/modulestructure/${fieldId}`)
            
            // Get the id of the item being deleted
            const i = this.items.map(o => o.id).indexOf(this.activeEditId)
            
            // Remove the item from state/the UI
            this.items.splice(i, 1)
            
            // Select the item before or after that delete
            const item = this.items[i - 1] || this.items[i]
            if (item) {
                this.activeEditId = item.id || 0
            }
            
            // If this is not the page component editor it must update the path
            if (!this.isContentType) {
                router.push({params: {fieldId: null}})
            }
        },
        
        async reloadFieldsets() {
            const fieldsetsStore = useFieldsetsStore()
            await fieldsetsStore.init()
            return this.setFieldsets()
        }
    }
})